/**
 * Kasumee analytics: Microsoft Clarity session tags + custom events.
 *
 * Clarity itself is loaded by the Microsoft Clarity app embed (project yz2rrjsmmw);
 * this file never loads a second copy. Every call is guarded so nothing breaks
 * when Clarity is blocked. Checkout is tracked by the Clarity app's Shopify pixel.
 *
 * Markup hooks:
 *   data-track="event_name"       tap/click (delegated on document)
 *   data-track-view="event_name"  fires once when >= 50% visible
 *   data-track-shown="event_name" fires once when the element gets data-stuck="true"
 *   <details data-track="...">    fires when the accordion is opened by the visitor
 *   seen_scarf_offer              scarf upsell card visible in the open cart drawer
 *                                 (the card arrives via a drawer re-render, so data-track-view can't see it)
 *   paypal_button tag             rendered / missing, 15s after the real PayPal block replaces the facade
 *
 * Debug: add ?debug_analytics=1 to the URL to log every call to the console.
 */
(() => {
  const script = document.currentScript;
  const params = new URLSearchParams(window.location.search);
  const debug = params.get('debug_analytics') === '1';

  /** @param {...any} args */
  const clarity = (...args) => {
    if (debug) console.log('[kasumee-analytics]', ...args);
    if (typeof window.clarity === 'function') window.clarity(...args);
  };

  const UPGRADES = {
    add_to_cart_main: 'add_to_cart',
    add_to_cart_sticky: 'add_to_cart',
    scarf_upsell_add: 'add_to_cart',
    buy_now_tap: 'checkout_tap',
    cart_checkout_tap: 'checkout_tap',
  };

  // The PayPal facade is replaced by the real iframe on first tap, so one intent can produce two taps.
  const ONCE_PER_PAGE = new Set(['gallery_swipe', 'buy_now_tap', 'client_love_swipe']);
  const fired = new Set();

  /** @param {string} name */
  const track = (name) => {
    if (!name) return;
    if (ONCE_PER_PAGE.has(name) || name.startsWith('seen_') || name === 'sticky_bar_shown') {
      if (fired.has(name)) return;
      fired.add(name);
    }
    clarity('event', name);
    if (UPGRADES[name]) clarity('upgrade', UPGRADES[name]);
  };

  /* ---------- Session tags ---------- */

  const UTM_KEYS = ['utm_source', 'utm_campaign', 'utm_content'];
  const STORAGE_KEY = 'kasumee_utm';

  /** @returns {Record<string, string>} */
  const readUtm = () => {
    const fromUrl = {};
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) fromUrl[key] = value.slice(0, 100);
    });

    try {
      if (Object.keys(fromUrl).length) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
        return fromUrl;
      }
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      return fromUrl;
    }
  };

  const data = script ? script.dataset : {};
  const pageType = ['product', 'cart', 'collection', 'index'].includes(data.pageType) ? data.pageType : 'other';

  clarity('set', 'page_type', pageType);
  // Shipping texts can depend on the buyer's market (Theme settings → Free shipping texts).
  if (window.Shopify && window.Shopify.country) clarity('set', 'market_country', window.Shopify.country);
  if (data.currency) clarity('set', 'currency', data.currency);
  if (pageType === 'product') {
    if (data.productHandle) clarity('set', 'product_handle', data.productHandle);
    clarity('set', 'template_suffix', data.templateSuffix || 'default');
  }
  Object.entries(readUtm()).forEach(([key, value]) => clarity('set', key, value));

  /* ---------- Taps (event delegation) ---------- */

  document.addEventListener(
    'click',
    (event) => {
      // Only real taps: the sticky bar forwards its tap to the main button with .click().
      if (!event.isTrusted) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const el = target.closest('[data-track]');
      if (!el) return;

      if (el.tagName === 'DETAILS') {
        // Accordions: only count the visitor opening it, not clicks inside the content.
        if (!target.closest('summary') || el.open) return;
        const name = el.getAttribute('data-track');
        setTimeout(() => {
          if (el.open) track(name);
        }, 300);
        return;
      }

      track(el.getAttribute('data-track'));
    },
    true
  );

  // Taps inside cross-origin iframes (PayPal / Google Pay buttons) never reach the document:
  // the window loses focus to the iframe instead.
  window.addEventListener('blur', () => {
    setTimeout(() => {
      const active = document.activeElement;
      if (!active) return;
      // Wallet buttons may keep their iframe inside a shadow root.
      let deepest = active;
      while (deepest.shadowRoot && deepest.shadowRoot.activeElement) deepest = deepest.shadowRoot.activeElement;
      if (deepest.tagName !== 'IFRAME') return;

      const el = active.closest('[data-track]');
      if (el) track(el.getAttribute('data-track'));
    }, 0);
  });

  // Variant picker: color option
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    if (!input.closest('variant-picker')) return;

    const option = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
    const optionName = option?.dataset.optionName || '';
    if (!/colou?r/i.test(optionName)) return;

    track('color_selected');
    clarity('set', 'selected_color', input.value);
  });

  /* ---------- Product gallery ---------- */

  document.addEventListener('slideshow:select', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    if (!detail.userInitiated || (detail.trigger !== 'scroll' && detail.trigger !== 'drag')) return;
    if (!(event.target instanceof Element) || !event.target.closest('media-gallery')) return;
    track('gallery_swipe');
  });

  /* ---------- Client Love ---------- */

  // The photo rail scrolls on its own (swipe, trackpad or the desktop arrows); scroll doesn't bubble,
  // so listen in the capture phase. Counted once per page.
  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target instanceof Element && event.target.matches('[data-cl-rail]')) track('client_love_swipe');
    },
    { capture: true, passive: true }
  );

  /* ---------- Cart ---------- */

  const trackScarfOfferSeen = () => {
    if (document.querySelector('#cart-drawer[open] kz-scarf-offer')) track('seen_scarf_offer');
  };

  document.addEventListener('theme-drawer:open', (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    track('cart_drawer_open');
    trackScarfOfferSeen();
  });

  // The card also appears inside an already open drawer (after adding a bag).
  document.addEventListener('kz-scarf-offer:connected', trackScarfOfferSeen);

  document.addEventListener('quantity-selector:update', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    if (detail.cartLine) track('cart_qty_changed');
  });

  /* ---------- PayPal button ---------- */

  // The PayPal button is a cross-origin iframe that Clarity can't record, so replays show a blank gap.
  // Tag whether the visitor's browser actually rendered it. The PayPal facade swaps in the real
  // Shopify block on the first tap/scroll, so the clock starts only when that block is on the page;
  // visitors who never interact get no tag.
  const PAYPAL_FRAME = 'iframe[name^="__zoid__paypal_buttons"]';
  const PAYPAL_TIMEOUT = 15000;

  if (pageType === 'product') {
    const pageStart = Date.now();
    let blockSince = 0;
    const paypalTimer = setInterval(() => {
      const block = document.querySelector('.accelerated-checkout-block');
      if (!block || block.hidden) {
        if (Date.now() - pageStart > 10 * 60 * 1000) clearInterval(paypalTimer);
        return;
      }
      if (!blockSince) blockSince = Date.now();

      const frame = block.querySelector(PAYPAL_FRAME);
      if (frame && frame.getBoundingClientRect().height > 20) {
        clarity('set', 'paypal_button', 'rendered');
        clearInterval(paypalTimer);
      } else if (Date.now() - blockSince >= PAYPAL_TIMEOUT) {
        clarity('set', 'paypal_button', 'missing');
        clearInterval(paypalTimer);
      }
    }, 1000);
  }

  /* ---------- Visibility ---------- */

  const observeViews = () => {
    // Product cards ("You may also like") reuse the price block; only the main product counts.
    const views = [...document.querySelectorAll('[data-track-view]')].filter((el) => !el.closest('product-card'));
    if (views.length && 'IntersectionObserver' in window) {
      /** @param {IntersectionObserverEntry[]} entries */
      const onIntersect = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) track(entry.target.getAttribute('data-track-view'));
        });
      };
      // >= 50% of the element is visible.
      const ratioObserver = new IntersectionObserver(onIntersect, { threshold: 0.5 });
      // Tall sections (Client Love is ~8700px on a phone) can never be 50% visible,
      // so also count an element once it reaches the middle of the screen.
      const middleObserver = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -50% 0px' });
      views.forEach((el) => {
        ratioObserver.observe(el);
        middleObserver.observe(el);
      });
    }

    document.querySelectorAll('[data-track-shown]').forEach((el) => {
      const check = () => {
        if (el.getAttribute('data-stuck') !== 'true') return false;
        track(el.getAttribute('data-track-shown'));
        return true;
      };
      if (check()) return;
      const mutation = new MutationObserver(() => {
        if (check()) mutation.disconnect();
      });
      mutation.observe(el, { attributes: true, attributeFilter: ['data-stuck'] });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeViews);
  } else {
    observeViews();
  }
})();
